/**
 * Fleet Reports Component
 *
 * Provides PDF export functionality for fleet-wide reports.
 * Used by DPA and Fleet Managers for surveyor audits and management reviews.
 *
 * Available Reports:
 * - Fleet Status Report: Vessel overview with RAG status
 * - Certificate Expiry Report: Fleet-wide certificate tracking
 * - Shore Edit Audit: All shore edits with acknowledgement status
 * - Escalation Report: Outstanding notifications requiring action
 *
 * @see FLEET-DASHBOARD-DESIGN.md Section 3.6
 */

import React, { useState } from 'react';
import {
  FileText,
  Ship,
  FileCheck,
  ClipboardList,
  AlertTriangle,
  Download,
  Calendar,
  Loader2,
  CheckCircle
} from 'lucide-react';

import { useFleetData } from '../../../../hooks/domain/fleet/useFleetData';
import { useFleetNotifications } from '../../../../hooks/domain/fleet/useFleetNotifications';
import { usePermissions } from '../../../../hooks/usePermissions';
import {
  exportFleetStatusPDF,
  exportCertificateExpiryPDF,
  exportShoreEditAuditPDF,
  exportEscalationReportPDF
} from '../../../../utils/pdf/fleetExport';

/**
 * Report Card Component
 */
const ReportCard = ({
  icon: Icon,
  title,
  description,
  onGenerate,
  isGenerating,
  lastGenerated,
  disabled,
  disabledReason
}) => {
  return (
    <div className={`
      bg-white rounded-xl shadow-sm border border-slate-200 p-6
      ${disabled ? 'opacity-60' : 'hover:shadow-md transition-shadow'}
    `}>
      <div className="flex items-start gap-4">
        <div className={`
          p-3 rounded-lg
          ${disabled ? 'bg-slate-100' : 'bg-cyan-50'}
        `}>
          <Icon className={`h-6 w-6 ${disabled ? 'text-slate-400' : 'text-cyan-600'}`} />
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <p className="text-sm text-slate-500 mt-1">{description}</p>

          {lastGenerated && (
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Last generated: {new Date(lastGenerated).toLocaleString('en-GB')}
            </p>
          )}

          {disabled && disabledReason && (
            <p className="text-xs text-amber-600 mt-2">{disabledReason}</p>
          )}
        </div>

        <button
          onClick={onGenerate}
          disabled={disabled || isGenerating}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
            transition-colors
            ${disabled || isGenerating
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-cyan-600 text-white hover:bg-cyan-700'
            }
          `}
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Generate PDF
            </>
          )}
        </button>
      </div>
    </div>
  );
};

/**
 * Fleet Reports Main Component
 */
const FleetReports = () => {
  const { currentUser } = usePermissions();
  const {
    vessels,
    fleetSummary,
    allCertificates
  } = useFleetData();
  const {
    getFleetNotifications,
    auditTrail
  } = useFleetNotifications();

  const [generatingReport, setGeneratingReport] = useState(null);
  const [lastGenerated, setLastGenerated] = useState({});

  // Get company name from vessels or user
  const companyName = vessels?.[0]?.companyName || currentUser?.organization || 'Fleet Management';

  /**
   * Generate Fleet Status Report
   */
  const handleFleetStatus = async () => {
    setGeneratingReport('fleet-status');
    try {
      const success = exportFleetStatusPDF(
        { vessels, fleetSummary },
        {
          companyName,
          generatedBy: currentUser?.name || 'DPA'
        }
      );
      if (success) {
        setLastGenerated(prev => ({
          ...prev,
          'fleet-status': new Date().toISOString()
        }));
      }
    } finally {
      setGeneratingReport(null);
    }
  };

  /**
   * Generate Certificate Expiry Report
   */
  const handleCertificateExpiry = async () => {
    setGeneratingReport('certificates');
    try {
      const success = exportCertificateExpiryPDF(
        allCertificates,
        {
          companyName,
          generatedBy: currentUser?.name || 'DPA',
          includeValid: true
        }
      );
      if (success) {
        setLastGenerated(prev => ({
          ...prev,
          'certificates': new Date().toISOString()
        }));
      }
    } finally {
      setGeneratingReport(null);
    }
  };

  /**
   * Generate Shore Edit Audit Report
   */
  const handleShoreEditAudit = async () => {
    setGeneratingReport('audit');
    try {
      const success = exportShoreEditAuditPDF(
        auditTrail,
        {
          companyName,
          generatedBy: currentUser?.name || 'DPA'
        }
      );
      if (success) {
        setLastGenerated(prev => ({
          ...prev,
          'audit': new Date().toISOString()
        }));
      }
    } finally {
      setGeneratingReport(null);
    }
  };

  /**
   * Generate Escalation Report
   */
  const handleEscalationReport = async () => {
    setGeneratingReport('escalation');
    try {
      const notifications = getFleetNotifications();
      const success = exportEscalationReportPDF(
        notifications,
        {
          companyName,
          generatedBy: currentUser?.name || 'DPA'
        }
      );
      if (success) {
        setLastGenerated(prev => ({
          ...prev,
          'escalation': new Date().toISOString()
        }));
      }
    } finally {
      setGeneratingReport(null);
    }
  };

  // Get counts for report descriptions
  const unacknowledgedNotifications = (getFleetNotifications() || []).filter(
    n => !n.acknowledgedAt && n.type === 'shore_edit'
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <FileText className="h-5 w-5 text-cyan-600" />
          Fleet Reports
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Generate PDF reports for surveyor audits and management reviews
        </p>
      </div>

      {/* Report Cards */}
      <div className="space-y-4">
        {/* Fleet Status Report */}
        <ReportCard
          icon={Ship}
          title="Fleet Status Report"
          description={`Overview of all ${vessels?.length || 0} vessels with compliance RAG status, expiring certificates, and open defects.`}
          onGenerate={handleFleetStatus}
          isGenerating={generatingReport === 'fleet-status'}
          lastGenerated={lastGenerated['fleet-status']}
          disabled={!vessels || vessels.length === 0}
          disabledReason="No vessel data available"
        />

        {/* Certificate Expiry Report */}
        <ReportCard
          icon={FileCheck}
          title="Certificate Expiry Report"
          description={`Fleet-wide certificate tracking with ${allCertificates?.length || 0} certificates. Highlights expired and expiring soon.`}
          onGenerate={handleCertificateExpiry}
          isGenerating={generatingReport === 'certificates'}
          lastGenerated={lastGenerated['certificates']}
          disabled={!allCertificates || allCertificates.length === 0}
          disabledReason="No certificate data available"
        />

        {/* Shore Edit Audit Report */}
        <ReportCard
          icon={ClipboardList}
          title="Shore Edit Audit Report"
          description={`ISM Code Section 9 compliant audit trail of ${auditTrail?.length || 0} shore edits with acknowledgement status.`}
          onGenerate={handleShoreEditAudit}
          isGenerating={generatingReport === 'audit'}
          lastGenerated={lastGenerated['audit']}
          disabled={!auditTrail || auditTrail.length === 0}
          disabledReason="No shore edits recorded yet"
        />

        {/* Escalation Report */}
        <ReportCard
          icon={AlertTriangle}
          title="Escalation Report"
          description={`Outstanding notifications requiring DPA action. Currently ${unacknowledgedNotifications.length} unacknowledged.`}
          onGenerate={handleEscalationReport}
          isGenerating={generatingReport === 'escalation'}
          lastGenerated={lastGenerated['escalation']}
          disabled={unacknowledgedNotifications.length === 0}
          disabledReason="No outstanding escalations"
        />
      </div>

      {/* Info Note */}
      <div className="mt-6 bg-slate-50 rounded-lg p-4 border border-slate-200">
        <div className="flex items-start gap-3">
          <Calendar className="h-5 w-5 text-slate-400 mt-0.5" />
          <div>
            <p className="text-sm text-slate-600">
              <strong>Report Generation</strong>
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Reports are generated as PDF files and downloaded to your device.
              They include the current date/time stamp and your name for audit purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetReports;
